'use client'

import React, { useState, useEffect, useRef, createContext, useContext, ReactNode, FC } from 'react'
import { onAuthStateChanged, User } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { updateLastAccess } from '@/lib/actions/updateLastAccess'
import { getDeviceType } from '@/lib/utils/login'
import { isStandalone } from '@/lib/utils/pwa'
import { LastAccessDevice } from '@/lib/interfaces/bar'

interface AuthContextProps {
  user: User | null;
}

const AuthContext = createContext<AuthContextProps>({ user: null })

export const useAuthContext = (): AuthContextProps => useContext(AuthContext)

interface AuthContextProviderProps {
  children: ReactNode;
}

const TRANSIENT_NULL_GRACE_MS = 1500
const AUTH_READY_TIMEOUT_MS = 2500
const FIREBASE_AUTH_STORAGE_PREFIX = 'firebase:authUser:'

// Devuelve true si Firebase todavía conserva datos de auth en localStorage.
// Cuando un signOut real ocurre (botón, token revocado confirmado) Firebase
// borra la clave `firebase:authUser:<apiKey>:<app>`. En un null espurio por
// sync entre pestañas la clave sigue presente.
const hasFirebaseAuthInStorage = (): boolean => {
  try {
    return Object.keys(localStorage).some((k) => k.startsWith(FIREBASE_AUTH_STORAGE_PREFIX))
  } catch {
    return false
  }
}

export const AuthContextProvider: FC<AuthContextProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const lastUserRef = useRef<User | null>(null)

  useEffect(() => {
    let nullTimer: ReturnType<typeof setTimeout> | null = null
    let firebaseResolved = false

    const readyTimeout = setTimeout(() => {
      if (!firebaseResolved) {
        setLoading(false)
      }
    }, AUTH_READY_TIMEOUT_MS)

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      firebaseResolved = true
      clearTimeout(readyTimeout)

      if (nullTimer !== null) {
        clearTimeout(nullTimer)
        nullTimer = null
      }

      if (firebaseUser) {
        lastUserRef.current = firebaseUser
        setUser(firebaseUser)
        const base = getDeviceType()
        const device: LastAccessDevice = isStandalone() ? (base === 'iphone' ? 'pwa-ios' : base === 'android' ? 'pwa-android' : 'pwa-pc') : (base as LastAccessDevice)
        updateLastAccess(firebaseUser.uid, device)
      } else {
        // Firebase emitió null. En un signOut real la clave
        // `firebase:authUser:*` desaparece de localStorage; en un null
        // espurio por sync multi-pestaña sigue ahí. Esperamos un poco y
        // comprobamos antes de propagarlo al contexto, para que un F5 en
        // otra pestaña no nos saque del dashboard.
        nullTimer = setTimeout(() => {
          if (!hasFirebaseAuthInStorage()) {
            lastUserRef.current = null
            setUser(null)
          } else if (lastUserRef.current) {
            // Mantén / restaura al último user conocido. Si el estado ya
            // coincide, esto es un no-op en React.
            setUser(lastUserRef.current)
          }
          nullTimer = null
        }, TRANSIENT_NULL_GRACE_MS)
      }
      setLoading(false)
    })

    return () => {
      if (nullTimer !== null) clearTimeout(nullTimer)
      clearTimeout(readyTimeout)
      unsubscribe()
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user }}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
